import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EasyCropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { supabase } from '../../supabaseClient';
import ProfileCardCachedImage from '../common/ProfileCardCachedImage';
import AutoStatusToast from '../common/AutoStatusToast';
import { useAutoStatusToast } from '../../hooks/useAutoStatusToast';
import {
  addWorkspaceProfileCardSlot,
  createWorkspaceProfileCardSet,
  deleteWorkspaceProfileCardSet,
  deleteWorkspaceProfileCardSlot,
  renameWorkspaceProfileCardSet,
  updateWorkspaceSlotRarity,
  uploadWorkspaceProfileCardSlotImage,
} from '../../services/workspaceProfileCardService';
import type {
  AdminProfileCardSlot,
  ProfileCardRarity,
  ProfileCardSetSource,
} from '../../types/profileCard';
import { MAX_WORKSPACE_PROFILE_CARD_SETS, MAX_PROFILE_CARD_SLOTS_PER_SET, profileCardSlotDisplayName } from '../../types/profileCard';
import { cropSquareToWebp, readFileAsDataURL, type CropAreaPixels } from '../../utils/imageCompress';
import {
  computeSlotDropPercents,
  profileCardExchangeCoinsForRarity,
  validateProfileCardSetRarities,
} from '../../utils/profileCardRarity';
import { putProfileCardImageCacheFromBlob } from '../../utils/profileCardImageCache';
import { readClipboardImageBlob } from '../../utils/slideImageCompress';
import ProfileCardRarityRulesGuide from './ProfileCardRarityRulesGuide';
import ProfileCardRaritySelect from './ProfileCardRaritySelect';
import '../../pages/AdminProfileCardSetsPage.css';
import './ProfileCardManageModal.css';

const FALLBACK_PREVIEW_BASE_PRICE_COINS = 50;

function resolvePreviewBasePrice(priceCoins?: number | null): number {
  if (typeof priceCoins === 'number' && Number.isFinite(priceCoins) && priceCoins >= 0) {
    return Math.round(priceCoins);
  }
  return FALLBACK_PREVIEW_BASE_PRICE_COINS;
}

const TrashIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 7H20M9 7V4.5C9 3.67157 9.67157 3 10.5 3H13.5C14.3284 3 15 3.67157 15 4.5V7M18.5 7L17.7 18.6C17.6 20 16.4 21 15 21H9C7.6 21 6.4 20 6.3 18.6L5.5 7"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 11V16M14 11V16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const Cropper = EasyCropper as unknown as React.ComponentType<{
  image?: string;
  crop: { x: number; y: number };
  zoom: number;
  aspect: number;
  cropShape?: 'rect' | 'round';
  showGrid?: boolean;
  onCropChange: (location: { x: number; y: number }) => void;
  onZoomChange?: (zoom: number) => void;
  onCropComplete?: (croppedArea: CropAreaPixels, croppedAreaPixels: CropAreaPixels) => void;
}>;

type DraftSlot = {
  localId: string;
  serverId: string | null;
  slotIndex: number;
  name: string;
  storagePath: string | null;
  imageVersion: number;
  rarity: ProfileCardRarity | null;
  localBlob: Blob | null;
  previewUrl: string | null;
};

export type ProfileCardManageTarget =
  | { mode: 'create'; priceCoins?: number | null }
  | {
      mode: 'edit';
      source: ProfileCardSetSource;
      setId: string;
      name: string;
      slots: AdminProfileCardSlot[];
      /** 아이템 카드 설정에 입력된 세트 구입 가격. 없으면 50코인 기준 */
      priceCoins?: number | null;
    };

type SlotUploadTarget = { localId: string; displayNumber: number };

function newLocalId(): string {
  return `slot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function slotsToDraft(slots: AdminProfileCardSlot[]): DraftSlot[] {
  return slots
    .slice()
    .sort((a, b) => a.slotIndex - b.slotIndex || a.id.localeCompare(b.id))
    .map((s) => ({
      localId: s.id || newLocalId(),
      serverId: s.id || null,
      slotIndex: s.slotIndex,
      name: s.name ?? '',
      storagePath: s.storagePath,
      imageVersion: s.imageVersion,
      rarity: s.rarity,
      localBlob: null,
      previewUrl: null,
    }));
}

function emptyDraftSlots(count = 10): DraftSlot[] {
  return Array.from({ length: count }, (_, i) => ({
    localId: newLocalId(),
    serverId: null,
    slotIndex: i + 1,
    name: '',
    storagePath: null,
    imageVersion: 0,
    rarity: null,
    localBlob: null,
    previewUrl: null,
  }));
}

function revokePreview(slot: DraftSlot) {
  if (slot.previewUrl) URL.revokeObjectURL(slot.previewUrl);
}

function serializeSlotBaseline(s: DraftSlot) {
  return {
    localId: s.localId,
    serverId: s.serverId,
    slotIndex: s.slotIndex,
    name: s.name,
    storagePath: s.storagePath,
    imageVersion: s.imageVersion,
    rarity: s.rarity,
    hasLocalBlob: Boolean(s.localBlob),
  };
}

async function fetchServerSlots(
  setId: string
): Promise<Array<{ id: string; slotIndex: number; storagePath: string | null }>> {
  const { data, error } = await supabase
    .from('workspace_profile_card_slots')
    .select('id, slot_index, storage_path')
    .eq('set_id', setId)
    .order('slot_index', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: String(r.id ?? ''),
    slotIndex: Number(r.slot_index) || 0,
    storagePath: typeof r.storage_path === 'string' ? r.storage_path : null,
  }));
}

const SlotUploadModal: React.FC<{
  target: SlotUploadTarget | null;
  onClose: () => void;
  onSave: (blob: Blob) => Promise<void>;
}> = ({ target, onClose, onSave }) => {
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const cropPixelsRef = React.useRef<CropAreaPixels | null>(null);

  useEffect(() => {
    setRawImageSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setSaving(false);
    setError('');
    setIsDragging(false);
    cropPixelsRef.current = null;
  }, [target]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('이미지 파일은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    setError('');
    setRawImageSrc(await readFileAsDataURL(file));
  }, []);

  useEffect(() => {
    if (!target || rawImageSrc) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const blob = readClipboardImageBlob(e.clipboardData);
      if (!blob) return;
      e.preventDefault();
      e.stopPropagation();
      const file =
        blob instanceof File
          ? blob
          : new File([blob], 'clipboard-image.png', { type: blob.type || 'image/png' });
      void handleFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [target, rawImageSrc, handleFile]);

  if (!target) return null;

  const handleSave = async () => {
    if (!rawImageSrc || !cropPixelsRef.current || saving) return;
    setSaving(true);
    setError('');
    try {
      const blob = await cropSquareToWebp(rawImageSrc, cropPixelsRef.current, 256, 0.82);
      await onSave(blob);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : '이미지 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="cp-pc-upload-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-pc-upload-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-pc-upload-header">
          <h3>{target.displayNumber}번 카드 이미지</h3>
          <button type="button" className="cp-pc-upload-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>
        {!rawImageSrc ? (
          <label
            className={`cp-pc-upload-dropzone${isDragging ? ' is-dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void handleFile(file);
            }}
          >
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            <span>이미지를 선택하거나 붙여넣기 / 드래그하세요</span>
          </label>
        ) : (
          <div className="cp-pc-upload-crop">
            <Cropper
              image={rawImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => {
                cropPixelsRef.current = pixels;
              }}
            />
          </div>
        )}
        {error ? <p className="cp-pc-upload-error">{error}</p> : null}
        {rawImageSrc ? (
          <label className="cp-pc-upload-zoom">
            <span>확대</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </label>
        ) : null}
        <div className="cp-pc-upload-actions">
          <button type="button" className="cp-pc-btn" onClick={onClose}>
            취소
          </button>
          {rawImageSrc ? (
            <button
              type="button"
              className="cp-pc-btn cp-pc-btn--primary"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? '적용 중…' : '적용'}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

const SlotMedia: React.FC<{
  source: ProfileCardSetSource;
  slot: DraftSlot;
  displayNumber: number;
  readOnly: boolean;
  onUploadClick: () => void;
}> = ({ source, slot, displayNumber, readOnly, onUploadClick }) => {
  const hasImage = Boolean(slot.previewUrl || slot.storagePath || slot.localBlob);
  const displayName = profileCardSlotDisplayName({ name: slot.name, slotIndex: displayNumber });

  return (
    <button
      type="button"
      className="cp-pc-slot-media"
      onClick={onUploadClick}
      disabled={readOnly}
      title={readOnly ? '관리자 세트는 읽기만 가능합니다' : undefined}
    >
      {hasImage ? (
        <ProfileCardCachedImage
          source={source}
          storagePath={slot.storagePath}
          imageVersion={slot.imageVersion}
          previewUrl={slot.previewUrl}
          alt={displayName}
        />
      ) : (
        <span className="cp-pc-slot-empty">{readOnly ? '—' : '+'}</span>
      )}
    </button>
  );
};

const SlotCard: React.FC<{
  source: ProfileCardSetSource;
  slot: DraftSlot;
  displayNumber: number;
  percent: number;
  basePriceCoins: number;
  readOnly: boolean;
  editingName: boolean;
  onUploadClick: () => void;
  onDeleteSlot: () => void;
  onRarityChange: (rarity: ProfileCardRarity) => void;
  onStartEditName: () => void;
  onNameChange: (name: string) => void;
  onEndEditName: () => void;
}> = ({
  source,
  slot,
  displayNumber,
  percent,
  basePriceCoins,
  readOnly,
  editingName,
  onUploadClick,
  onDeleteSlot,
  onRarityChange,
  onStartEditName,
  onNameChange,
  onEndEditName,
}) => {
  const hasImage = Boolean(slot.previewUrl || slot.storagePath || slot.localBlob);
  const expectedExchangeCoins = profileCardExchangeCoinsForRarity(basePriceCoins, slot.rarity);
  const displayName = profileCardSlotDisplayName({ name: slot.name, slotIndex: displayNumber });
  return (
    <div
      className={`cp-pc-slot${hasImage ? ' has-image' : ''}${readOnly ? ' is-readonly' : ''}${
        slot.rarity ? ` is-rarity-${slot.rarity}` : ''
      }`}
    >
      {editingName && !readOnly ? (
        <input
          type="text"
          className="cp-pc-slot-name-input"
          value={slot.name}
          placeholder={`${displayNumber}번 카드`}
          maxLength={40}
          autoFocus
          aria-label={`${displayNumber}번 카드 이름`}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={onEndEditName}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              onEndEditName();
            }
          }}
        />
      ) : (
        <button
          type="button"
          className="cp-pc-slot-index"
          onClick={() => {
            if (!readOnly) onStartEditName();
          }}
          disabled={readOnly}
          title={readOnly ? undefined : '클릭하여 카드 이름 설정'}
          aria-label={
            readOnly ? `${displayNumber}번 카드` : `${displayNumber}번 카드 이름 설정`
          }
        >
          <span className="cp-pc-slot-index-num">{displayNumber}</span>
          <span className="cp-pc-slot-index-name">{displayName}</span>
        </button>
      )}
      <SlotMedia
        source={source}
        slot={slot}
        displayNumber={displayNumber}
        readOnly={readOnly}
        onUploadClick={onUploadClick}
      />
      <div className="cp-pc-slot-weight">
        <span className="cp-pc-slot-weight-label">카드 등급</span>
        <ProfileCardRaritySelect
          value={slot.rarity}
          disabled={readOnly || !hasImage}
          aria-label={`${displayNumber}번 카드 등급`}
          onChange={onRarityChange}
        />
      </div>
      <div className="cp-pc-slot-percent">
        {hasImage && slot.rarity ? `뽑힐 확률 ${percent.toFixed(1)}%` : '뽑힐 확률 -'}
      </div>
      <div className="cp-pc-slot-exchange">
        <div className="cp-pc-slot-exchange-coins">
          예상 환전 코인 {hasImage && slot.rarity ? expectedExchangeCoins : '-'}
        </div>
        <div className="cp-pc-slot-exchange-base">
          <span>예상 구입단가</span>
          <span>{basePriceCoins}코인 기준</span>
        </div>
      </div>
      {!readOnly ? (
        <button
          type="button"
          className="cp-pc-slot-remove"
          onClick={onDeleteSlot}
          aria-label="카드 삭제"
          title="카드 삭제"
        >
          <TrashIcon />
        </button>
      ) : null}
    </div>
  );
};

type Props = {
  open: boolean;
  workspaceId: string;
  target: ProfileCardManageTarget | null;
  existingUserSetCount?: number;
  onClose: () => void;
  onSaved?: () => void;
};

const ProfileCardManageModal: React.FC<Props> = ({
  open,
  workspaceId,
  target,
  existingUserSetCount = 0,
  onClose,
  onSaved,
}) => {
  const [nameDraft, setNameDraft] = useState('');
  const [baselineName, setBaselineName] = useState('');
  const [draftSlots, setDraftSlots] = useState<DraftSlot[]>([]);
  const [baselineSlotsJson, setBaselineSlotsJson] = useState('');
  const [initialServerSlots, setInitialServerSlots] = useState<
    Array<{ id: string; slotIndex: number }>
  >([]);
  const [serverSetId, setServerSetId] = useState<string | null>(null);
  const [source, setSource] = useState<ProfileCardSetSource>('workspace');
  const [isCreate, setIsCreate] = useState(false);
  const [uploadTarget, setUploadTarget] = useState<SlotUploadTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [editingNameSlotId, setEditingNameSlotId] = useState<string | null>(null);
  const { toastState, showError, clearToast } = useAutoStatusToast(3500);
  const draftSlotsRef = useRef<DraftSlot[]>([]);
  draftSlotsRef.current = draftSlots;

  const readOnly = source === 'admin';
  const basePriceCoins = resolvePreviewBasePrice(target?.priceCoins);

  useEffect(() => {
    if (!open || !target) return;
    draftSlotsRef.current.forEach(revokePreview);
    clearToast();
    setConfirmDiscard(false);
    setUploadTarget(null);
    setEditingNameSlotId(null);
    setSaving(false);

    if (target.mode === 'create') {
      const name = `사용자 세트${Math.min(existingUserSetCount + 1, MAX_WORKSPACE_PROFILE_CARD_SETS)}`;
      const slots = emptyDraftSlots(10);
      setIsCreate(true);
      setSource('workspace');
      setServerSetId(null);
      setNameDraft(name);
      setBaselineName(name);
      setDraftSlots(slots);
      setBaselineSlotsJson(JSON.stringify(slots.map(serializeSlotBaseline)));
      setInitialServerSlots([]);
      return;
    }

    const slots = slotsToDraft(target.slots);
    setIsCreate(false);
    setSource(target.source);
    setServerSetId(target.setId);
    setNameDraft(target.name);
    setBaselineName(target.name);
    setDraftSlots(slots);
    setBaselineSlotsJson(JSON.stringify(slots.map(serializeSlotBaseline)));
    setInitialServerSlots(
      slots
        .filter((s) => s.serverId)
        .map((s) => ({ id: s.serverId as string, slotIndex: s.slotIndex }))
    );
  }, [open, target, existingUserSetCount, clearToast]);

  useEffect(() => {
    return () => {
      draftSlotsRef.current.forEach(revokePreview);
    };
  }, []);

  const dirty = useMemo(() => {
    if (readOnly) return false;
    if (nameDraft.trim() !== baselineName.trim()) return true;
    return JSON.stringify(draftSlots.map(serializeSlotBaseline)) !== baselineSlotsJson;
  }, [readOnly, nameDraft, baselineName, draftSlots, baselineSlotsJson]);

  const slotPercents = useMemo(() => {
    const likes = draftSlots.map((s) => ({
      rarity: s.rarity,
      hasImage: Boolean(s.previewUrl || s.storagePath || s.localBlob),
    }));
    return computeSlotDropPercents(likes);
  }, [draftSlots]);

  const requestClose = () => {
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const patchSlot = (localId: string, patch: Partial<DraftSlot>) => {
    setDraftSlots((prev) =>
      prev.map((s) => {
        if (s.localId !== localId) return s;
        if (patch.previewUrl !== undefined && s.previewUrl && s.previewUrl !== patch.previewUrl) {
          revokePreview(s);
        }
        return { ...s, ...patch };
      })
    );
  };

  const handleUploadApply = async (blob: Blob) => {
    if (!uploadTarget) return;
    const current = draftSlotsRef.current.find((s) => s.localId === uploadTarget.localId);
    const previewUrl = URL.createObjectURL(blob);
    patchSlot(uploadTarget.localId, {
      localBlob: blob,
      previewUrl,
      rarity: current?.rarity ?? 'normal',
    });
  };

  const handleAddSlot = () => {
    if (readOnly) return;
    if (draftSlots.length >= MAX_PROFILE_CARD_SLOTS_PER_SET) {
      showError(`프로필 카드는 세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개까지 등록할 수 있습니다.`);
      return;
    }
    const nextIndex = Math.max(0, ...draftSlots.map((s) => s.slotIndex)) + 1;
    const localId = newLocalId();
    setDraftSlots((prev) => [
      ...prev,
      {
        localId,
        serverId: null,
        slotIndex: nextIndex,
        name: '',
        storagePath: null,
        imageVersion: 0,
        rarity: null,
        localBlob: null,
        previewUrl: null,
      },
    ]);
    setUploadTarget({ localId, displayNumber: draftSlots.length + 1 });
  };

  const handleDeleteSlot = (slot: DraftSlot, displayNumber: number) => {
    if (readOnly) return;
    if (!window.confirm(`${displayNumber}번 카드를 삭제할까요? (저장하기를 눌러야 반영됩니다)`)) return;
    revokePreview(slot);
    setDraftSlots((prev) => prev.filter((s) => s.localId !== slot.localId));
  };

  const handleDeleteSet = async () => {
    if (readOnly || isCreate || !serverSetId) return;
    if (
      !window.confirm(`"${nameDraft.trim() || '이 세트'}"를 삭제할까요? 등록된 이미지도 함께 삭제됩니다.`)
    ) {
      return;
    }
    setSaving(true);
    clearToast();
    try {
      await deleteWorkspaceProfileCardSet(serverSetId);
      onSaved?.();
      onClose();
    } catch (e) {
      showError(e instanceof Error ? e.message : '세트 삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (readOnly || saving) return;
    const name = nameDraft.trim();
    if (!name) {
      showError('세트 이름을 입력해 주세요.');
      return;
    }
    const rarityErr = validateProfileCardSetRarities(
      draftSlots.map((s) => ({
        rarity: s.rarity,
        hasImage: Boolean(s.previewUrl || s.storagePath || s.localBlob),
      }))
    );
    if (rarityErr) {
      showError(rarityErr);
      return;
    }
    setSaving(true);
    clearToast();
    try {
      let setId = serverSetId;
      if (isCreate || !setId) {
        setId = await createWorkspaceProfileCardSet(workspaceId, name, existingUserSetCount + 1);
      } else if (name !== baselineName.trim()) {
        await renameWorkspaceProfileCardSet(setId, name);
      }

      const keptServerIds = new Set(
        draftSlots.map((s) => s.serverId).filter((id): id is string => Boolean(id))
      );

      // 편집: draft에서 빠진 기존 슬롯 삭제
      for (const base of initialServerSlots) {
        if (!keptServerIds.has(base.id)) {
          await deleteWorkspaceProfileCardSlot(setId, base.slotIndex);
        }
      }

      let unusedServer = (await fetchServerSlots(setId))
        .slice()
        .sort((a, b) => a.slotIndex - b.slotIndex);

      for (const draft of draftSlots) {
        let server = draft.serverId
          ? unusedServer.find((s) => s.id === draft.serverId) ?? null
          : null;

        if (server) {
          unusedServer = unusedServer.filter((s) => s.id !== server!.id);
        } else if (isCreate && unusedServer.length > 0) {
          server = unusedServer.shift() ?? null;
        }

        if (!server) {
          const added = await addWorkspaceProfileCardSlot(setId);
          server = {
            id: added.id,
            slotIndex: added.slotIndex,
            storagePath: added.storagePath,
          };
        }

        if (draft.localBlob) {
          const uploaded = await uploadWorkspaceProfileCardSlotImage({
            workspaceId,
            setId,
            slotIndex: server.slotIndex,
            blob: draft.localBlob,
            previousPath: server.storagePath ?? draft.storagePath,
          });
          await putProfileCardImageCacheFromBlob(
            'workspace',
            uploaded.storagePath,
            uploaded.imageVersion,
            draft.localBlob
          );
        }

        const hasImage = Boolean(draft.localBlob || draft.storagePath || server.storagePath);
        await updateWorkspaceSlotRarity(server.id, hasImage ? draft.rarity : null, draft.name);
      }

      // 생성 시 draft보다 남은 기본 슬롯 정리
      for (const leftover of unusedServer) {
        await deleteWorkspaceProfileCardSlot(setId, leftover.slotIndex);
      }

      onSaved?.();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '저장에 실패했습니다.';
      if (/['"]name['"].*workspace_profile_card_slots|workspace_profile_card_slots.*['"]name['"]|schema cache/i.test(msg)) {
        showError(
          '카드 이름 컬럼이 DB에 없습니다. Supabase에서 마이그레이션(20260809220000_profile_card_slot_name.sql)을 적용한 뒤 다시 저장해 주세요.'
        );
      } else {
        showError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open || !target) return null;

  return createPortal(
    <div className="cp-pc-manage-overlay" role="presentation" onClick={requestClose}>
      <div
        className="cp-pc-manage-modal"
        data-tutorial-pin="profile-card-manage-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pc-manage-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-pc-manage-header">
          <div className="cp-pc-manage-title-stack">
            <h2 id="cp-pc-manage-title">프로필 카드 관리</h2>
            <ProfileCardRarityRulesGuide />
            <div className="cp-pc-manage-legend" aria-label="세트 범주">
              <span className="cp-pc-manage-legend-item">
                <span
                  className="cp-admin-learning-template-dot cp-admin-learning-template-dot--default"
                  aria-hidden
                />
                관리자 세트
              </span>
              <span className="cp-pc-manage-legend-item">
                <span
                  className="cp-admin-learning-template-dot cp-admin-learning-template-dot--user"
                  aria-hidden
                />
                사용자 세트
              </span>
            </div>
          </div>
          <button type="button" className="cp-pc-manage-close" onClick={requestClose} aria-label="닫기">
            ×
          </button>
        </div>

        <div className="cp-pc-manage-body">
          <div className="cp-pc-set-panel">
            <div className="cp-pc-set-title-block">
              <div className="cp-pc-set-title-main">
                <span
                  className={`cp-admin-learning-template-dot ${
                    source === 'admin'
                      ? 'cp-admin-learning-template-dot--default'
                      : 'cp-admin-learning-template-dot--user'
                  }`}
                  aria-hidden
                />
                {readOnly ? (
                  <span className="cp-pc-set-name-readonly">{nameDraft}</span>
                ) : (
                  <input
                    type="text"
                    className="cp-pc-set-name-input"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    placeholder="세트 이름"
                  />
                )}
              </div>
              <div className="cp-pc-set-title-actions">
                {!readOnly && !isCreate ? (
                  <button
                    type="button"
                    className="cp-pc-btn cp-pc-btn--danger"
                    onClick={() => void handleDeleteSet()}
                    disabled={saving}
                  >
                    세트 삭제
                  </button>
                ) : readOnly ? (
                  <span className="cp-pc-manage-readonly-badge">읽기 전용</span>
                ) : null}
              </div>
            </div>
            <div className="cp-pc-slot-grid">
              {draftSlots.map((slot, index) => {
                const displayNumber = index + 1;
                return (
                  <SlotCard
                    key={slot.localId}
                    source={source}
                    slot={slot}
                    displayNumber={displayNumber}
                    percent={slotPercents[index] ?? 0}
                    basePriceCoins={basePriceCoins}
                    readOnly={readOnly}
                    editingName={editingNameSlotId === slot.localId}
                    onUploadClick={() => {
                      if (readOnly) return;
                      setUploadTarget({ localId: slot.localId, displayNumber });
                    }}
                    onDeleteSlot={() => handleDeleteSlot(slot, displayNumber)}
                    onRarityChange={(r) => patchSlot(slot.localId, { rarity: r })}
                    onStartEditName={() => setEditingNameSlotId(slot.localId)}
                    onNameChange={(name) => patchSlot(slot.localId, { name })}
                    onEndEditName={() => setEditingNameSlotId(null)}
                  />
                );
              })}
              {!readOnly ? (
                <button
                  type="button"
                  className="cp-pc-slot-add"
                  onClick={handleAddSlot}
                  disabled={saving || draftSlots.length >= MAX_PROFILE_CARD_SLOTS_PER_SET}
                  title={
                    draftSlots.length >= MAX_PROFILE_CARD_SLOTS_PER_SET
                      ? `세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개`
                      : undefined
                  }
                >
                  <span className="cp-pc-slot-add-plus" aria-hidden>
                    +
                  </span>
                  <span>카드 추가</span>
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="cp-pc-manage-footer">
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
            onClick={requestClose}
            disabled={saving}
          >
            닫기
          </button>
          {!readOnly ? (
            <button
              type="button"
              className="cp-pin-modal-btn cp-pin-modal-btn--primary"
              onClick={() => void handleSave()}
              disabled={saving || (!dirty && !isCreate)}
            >
              {saving ? '저장 중…' : '저장하기'}
            </button>
          ) : null}
        </div>

        {confirmDiscard ? (
          <div
            className="cp-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-pc-manage-leave-title"
            onClick={() => setConfirmDiscard(false)}
          >
            <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
              <h3 id="cp-pc-manage-leave-title" className="cp-leave-modal-title">
                변경사항이 저장되지 않았습니다
              </h3>
              <p className="cp-leave-modal-desc">저장하지 않고 나가시겠습니까?</p>
              <div className="cp-modal-footer cp-leave-modal-actions">
                <button
                  type="button"
                  className="cp-btn-secondary"
                  onClick={() => {
                    setConfirmDiscard(false);
                    onClose();
                  }}
                  disabled={saving}
                >
                  저장하지 않고 나가기
                </button>
                <button
                  type="button"
                  className="cp-btn-primary"
                  onClick={() => {
                    setConfirmDiscard(false);
                    void handleSave();
                  }}
                  disabled={saving}
                >
                  {saving ? '저장 중…' : '저장 하기'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <SlotUploadModal
          target={uploadTarget}
          onClose={() => setUploadTarget(null)}
          onSave={handleUploadApply}
        />
        {toastState ? (
          <AutoStatusToast state={toastState} className="cp-toast-auto-over-modal" />
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default ProfileCardManageModal;
